/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { getPublicSiteUrl } from "@cosmos/common-utils";
import { CosmosComponentContext, CosmosComponentContextProps, GameModeContext } from "@cosmos/components";
import { EDSProvider, lowDensityThemeClass } from "@eds/react";
import { LocationProvider } from "@epic-mw/app-context";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

interface Props {
	children?: React.ReactNode;
	componentContext?: CosmosComponentContextProps;
	/**
	 * `?mode=game` / `?mode=iosgame`. Provided as context so every CosmosRichText
	 * in the tree neutralises its links, including rich text rendered by
	 * components that don't forward the flag. See docs/game-mode.md.
	 */
	isGameMode?: boolean;
}

export const TemplateProviders = ({ children, componentContext, isGameMode }: Props): React.JSX.Element => {
	const [hash, setHash] = useState("");
	useEffect(() => {
		if (typeof window !== "undefined" && window?.location?.hash && hash !== window?.location?.hash) {
			setHash(window.location.hash);
		}
	}, [hash]);
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const search = searchParams.toString();
	const searchStr = search && search.length ? `?${search}` : "";
	// getPublicSiteUrl() returns origin + base path (e.g., "https://www.fab.com/o").
	// usePathname() returns the path relative to basePath (e.g., "/en-US/page"), so no double-prefix.
	const urlStr = `${getPublicSiteUrl()}${pathname}${searchStr}${hash}`;
	// Memoized on the string, not rebuilt per render. LocationProvider's useMemo
	// keys on this object, so a fresh URL each render gave every consumer a new
	// reference — re-firing useFormatHtml's cleanup effect and re-running the
	// formatHtml pipeline for every rich-text block on the page, on all traffic.
	const location = useMemo(() => new URL(urlStr), [urlStr]);

	const content = componentContext ? (
		<CosmosComponentContext.Provider value={componentContext}>{children}</CosmosComponentContext.Provider>
	) : (
		<>{children}</>
	);

	return (
		<EDSProvider dir={"ltr"} themeClass={lowDensityThemeClass}>
			<GameModeContext.Provider value={!!isGameMode}>
				<LocationProvider location={location}>{content}</LocationProvider>
			</GameModeContext.Provider>
		</EDSProvider>
	);
};
